import dayjs from 'dayjs';
import { z } from 'zod';

// Accepts either HH:MM or a UTC ISO string
export const timeSchema = z
  .string()
  .refine(
    (val) =>
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(val) || // HH:MM
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(val), // UTC ISO
    {
      message: 'Time must be in HH:MM format or UTC ISO string',
    }
  )
  .or(z.null());

// Day hours schema
export const dayHoursSchema = z.object({
  open: timeSchema,
  close: timeSchema,
  isClosed: z.boolean().optional().default(false)
}).refine((data) => {
  // If not closed, both open and close times must be provided
  if (!data.isClosed && (data.open === null || data.close === null)) {
    return false
  }
  // If closed, times can be null
  if (data.isClosed) {
    return true
  }
  // Validate that close time is after open time
  if (data.open && data.close) {
    const open = dayjs(data.open)
    const close = dayjs(data.close)
    return open.isValid() && close.isValid() && close.isAfter(open)
  }
  return true
}, {
  message: 'Close time must be after open time, and both times are required when not closed'
})

// Opening hours schema
export const openingHoursSchema = z.object({
  monday: dayHoursSchema,
  tuesday: dayHoursSchema,
  wednesday: dayHoursSchema,
  thursday: dayHoursSchema,
  friday: dayHoursSchema,
  saturday: dayHoursSchema,
  sunday: dayHoursSchema
})

// Special hours schema (key-based structure)
export const specialHoursSchema = z.record(
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  z.object({
    open: timeSchema,
    close: timeSchema,
    note: z.string().optional()
  }).refine((data) => {
    // If both times are null, it's closed
    if (data.open === null && data.close === null) {
      return true
    }
    // If one time is provided, both must be provided
    if ((data.open === null) !== (data.close === null)) {
      return false
    }
    // Validate that close time is after open time
    if (data.open && data.close) {
      const open = dayjs(data.open)
      const close = dayjs(data.close)
      return open.isValid() && close.isValid() && close.isAfter(open)
    }
    return true
  }, {
    message: 'Close time must be after open time, and both times are required when not closed'
  })
)

// Holidays schema (key-based structure)
export const holidaysSchema = z.record(
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  z.object({
    name: z.string().min(1, 'Holiday name is required'),
    isClosed: z.boolean().optional().default(true),
    note: z.string().optional()
  })
)

// Simple holidays schema (current DB format)
export const simpleHolidaysSchema = z.record(
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  z.string().min(1, 'Holiday name is required')
)

// Complete location hours data schema
export const locationHoursDataSchema = z.object({
  openingHours: openingHoursSchema.optional().nullable(),
  specialHours: specialHoursSchema.optional().nullable(),
  holidays: z.union([holidaysSchema, simpleHolidaysSchema]).optional().nullable()
})
